import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronDown, TableIcon } from 'lucide-react'
import type { ToolCallDisplay } from '@/types'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { SqlCodeBlock } from '@/components/SqlCodeBlock'
import { PythonCodeBlock } from '@/components/PythonCodeBlock'
import { JsonCodeBlock } from '@/components/JsonCodeBlock'
import { RunCodeResult, isRunCodeResult } from '@/components/RunCodeResult'
import {
  parseSqlResult,
  type SqlResultData,
} from '@/components/SqlResultsTable'
import { convertToolName, getToolIcon } from '@/utils/toolConfig'
import { useConfiguration } from '@/hooks/useConfiguration'
import { useShellHref } from '@/utils/router'
import { cn } from '@/lib/utils'

import {
  TOOL_CHEVRON_CLASSNAME,
  TOOL_ICON_CLASSNAME,
  TOOL_TRIGGER_CLASSNAME,
} from './toolTriggerStyles'

function Section({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 bg-black-alpha-5 rounded-lg',
        className
      )}>
      <p className="text-sm text-black-alpha-10 leading-normal font-normal">
        {label}
      </p>
      {children}
    </div>
  )
}

function ToolArgs({ args }: { args?: Record<string, unknown> }) {
  if (!args || Object.keys(args).length === 0) return null

  let body: React.ReactNode
  if (args.sql) {
    const otherArgs = Object.fromEntries(
      Object.entries(args).filter(([k]) => k !== 'sql')
    )
    body = (
      <>
        <SqlCodeBlock sql={String(args.sql)} />
        {Object.keys(otherArgs).length > 0 && (
          <JsonCodeBlock value={otherArgs} />
        )}
      </>
    )
  } else if (args.code) {
    body = <PythonCodeBlock code={String(args.code)} />
  } else {
    body = <JsonCodeBlock value={args} />
  }

  return (
    <Section label="Parameters" className="p-6 overflow-hidden">
      {body}
    </Section>
  )
}

interface SqlResultLinkProps {
  sqlResult: SqlResultData
  toolCallId?: string
  shellHref: (path: string) => string
}

function SqlResultLink({
  sqlResult,
  toolCallId,
  shellHref,
}: SqlResultLinkProps) {
  const [searchParams] = useSearchParams()

  // The payload rides in nav state as a fast path, but state dies when the tab
  // is backgrounded — so the identity goes in the URL, which the tab keeps.
  // `agentId`/`conversation`, not the `agent`/`conversationId` shell keys:
  // taskSearchOf strips shell keys from the stored tab href. Each id is
  // therefore carried TWICE on purpose — shellHref re-adds the shell copy for
  // the chat panel, and only the task-owned copy survives onto the tab.
  const agentId = searchParams.get('agent')
  const conversationId = searchParams.get('conversationId')
  const query = new URLSearchParams()
  if (agentId) query.set('agentId', agentId)
  if (conversationId) query.set('conversation', conversationId)
  if (toolCallId) query.set('toolCallId', toolCallId)
  const search = query.toString()

  // A fresh state object each render would defeat Link's memoization.
  const linkState = useMemo(() => ({ result: sqlResult }), [sqlResult])

  return (
    <Link
      state={linkState}
      to={shellHref(`/query-results${search ? `?${search}` : ''}`)}
      className="inline-flex items-center gap-1 text-xs text-primary font-medium underline">
      <TableIcon className="size-4" />
      View results table
    </Link>
  )
}

interface ToolResultProps {
  event: ToolCallDisplay
  contentPanelQueryResultsEnabled: boolean
  shellHref: (path: string) => string
}

function ToolResult({
  event,
  contentPanelQueryResultsEnabled,
  shellHref,
}: ToolResultProps) {
  // Parse once per result — tool messages re-render every frame while a
  // response streams, and results can be large.
  const parsed = useMemo<unknown>(() => {
    if (typeof event.result !== 'string') return event.result
    try {
      return JSON.parse(event.result)
    } catch {
      return event.result
    }
  }, [event.result])

  if (!event.result) return null

  if (event.toolName === 'run_code' && isRunCodeResult(parsed)) {
    return (
      <Section label="Results" className="p-4">
        <RunCodeResult result={parsed} />
      </Section>
    )
  }

  const sqlResult =
    contentPanelQueryResultsEnabled && event.toolName === 'execute_sql'
      ? parseSqlResult(parsed)
      : null
  if (sqlResult) {
    const rowCount = sqlResult.count ?? sqlResult.data?.length ?? 0
    return (
      <Section label="Results" className="p-4">
        {sqlResult.success ? (
          <div className="flex items-center justify-between bg-success-ui-bg rounded px-3 py-3">
            <span className="text-xs font-medium text-success-solid">
              ✓ Query successful
            </span>
            {rowCount > 0 && (
              <span className="text-xs font-medium text-success-solid">
                {rowCount} row{rowCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        ) : (
          <div className="text-sm text-destructive">
            ✗ Query failed
            {typeof sqlResult.error === 'string' && `: ${sqlResult.error}`}
          </div>
        )}
        {sqlResult.success && sqlResult.data?.length > 0 && (
          <SqlResultLink
            sqlResult={sqlResult}
            toolCallId={event.toolCallId}
            shellHref={shellHref}
          />
        )}
      </Section>
    )
  }

  return (
    <Section label="Results" className="p-6">
      {typeof parsed === 'string' ? (
        // Non-JSON string result (JSON.parse failed) — show as-is.
        <pre className="text-xs text-foreground whitespace-pre-wrap break-words">
          {parsed}
        </pre>
      ) : (
        <JsonCodeBlock value={parsed} />
      )}
    </Section>
  )
}

function DefaultTrigger({ event }: { event: ToolCallDisplay }) {
  const Icon = getToolIcon(event.toolName)
  return (
    <>
      <Icon aria-hidden="true" className={TOOL_ICON_CLASSNAME} />
      <span className="whitespace-nowrap">
        {convertToolName(event.toolName, event.status !== 'running')}
      </span>
    </>
  )
}

interface ToolCallMessageProps {
  event: ToolCallDisplay
  // Replaces the default icon + title; the chevron and body stay.
  trigger?: React.ReactNode
}

export function ToolCallMessage({ event, trigger }: ToolCallMessageProps) {
  const shellHref = useShellHref()
  const { configuration } = useConfiguration()
  const contentPanelQueryResultsEnabled =
    configuration?.features?.content_panel_query_results ?? false

  return (
    <Collapsible className="flex flex-col items-start gap-2 w-full">
      <CollapsibleTrigger className={TOOL_TRIGGER_CLASSNAME}>
        {trigger ?? <DefaultTrigger event={event} />}
        <ChevronDown aria-hidden="true" className={TOOL_CHEVRON_CLASSNAME} />
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-2 w-full data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <ToolArgs args={event.args} />
        <ToolResult
          event={event}
          contentPanelQueryResultsEnabled={contentPanelQueryResultsEnabled}
          shellHref={shellHref}
        />
      </CollapsibleContent>
    </Collapsible>
  )
}
